import type { HTTP } from "@/core/http";
import { formatGroupedNumber } from "../utils/romDisplay";
import type {
  DevelopPreview,
  DevelopRomInput,
  DevelopRomResult,
  RomDevelopApi,
  RomEnumApi,
  RomMetadata,
  RomMetadataApi,
  RomPhase,
  RomPhaseApi,
  RomPhaseHistory,
  RomPhaseHistoryApi,
  RomPreviewApi,
  RomRole,
  RomStatus,
  RomSummary,
  RomSummaryApi,
  RomSummaryHistory,
  RomSummaryHistoryApi,
  UpdateRomPhaseInput,
  UpdateRomSummaryInput,
} from "../types";

const BASE = "/pms/rom-effort";

const RECALCULATION_WARNING =
  "Recalculating will update the effort from the latest Business Requirements. Any effort changes you made will be replaced. Role, FTE, percentages and rates will be kept. Continue?";

function isNotFoundError(error: unknown): boolean {
  return error instanceof Error && /not found/i.test(error.message);
}

function asNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function asNumberOrZero(value: number | string | null | undefined): number {
  return asNumber(value) ?? 0;
}

function flagYes(flag: RomEnumApi | null | undefined): boolean {
  return String(flag?.enum ?? "").toUpperCase() === "Y";
}

function normalizeStatus(raw: RomEnumApi | null | undefined): RomStatus {
  const value = String(raw?.enum ?? "").toUpperCase();
  if (value === "OPEN" || value === "APPROVED" || value === "DRAFT") return value;
  return "DRAFT";
}

function dateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : value;
}

function nyAckMessage(count: number): string {
  return `I understand that ${count} Business Requirements are not estimated.`;
}

function formatChangeHours(value: number | string | null | undefined): string {
  return formatGroupedNumber(asNumberOrZero(value));
}

export function mapRomSummary(raw: RomSummaryApi): RomSummary {
  const status = normalizeStatus(raw.status);
  return {
    projectId: raw.projectId,
    techStack: raw.estimationBasisTechStack ?? "—",
    domain: raw.estimationBasisDomain ?? "—",
    plannedStartDate: dateOnly(raw.plannedStartDate) ?? "",
    plannedEndDate: dateOnly(raw.plannedEndDate),
    devEffort: asNumberOrZero(raw.developmentEffort),
    estTotalEffort: asNumberOrZero(raw.estimatedTotalEffort),
    finalTotalEffort: asNumberOrZero(raw.finalTotalEffort),
    totalDurationDays: raw.totalDurationDays ?? 0,
    totalDurationWeeks: raw.totalDurationWeeks ?? 0,
    durationMonths: raw.durationMonths ?? 0,
    sprintCount: raw.sprintCount ?? 0,
    manpowerCost: asNumber(raw.manpowerCost),
    brCount: raw.businessRequirementCount ?? 0,
    brNyCount: raw.notYetEstimatedCount ?? 0,
    status,
    statusLabel: raw.status?.label ?? status,
    remarks: raw.remarks ?? "",
    hoursPerDay: asNumberOrZero(raw.hoursPerDay),
    sprintLengthDays: raw.sprintLengthDays ?? 0,
    developedAt: raw.developedAt ?? null,
    updatedAt: raw.updatedAt ?? "",
    updatedBy: raw.updatedBy ?? "",
  };
}

export function mapRomPhase(raw: RomPhaseApi): RomPhase {
  return {
    id: raw.romPhId,
    projectId: raw.projectId,
    name: raw.phaseName ?? "",
    sequence: raw.phaseSequence ?? 0,
    percentage: asNumberOrZero(raw.percentage),
    estEffort: asNumberOrZero(raw.estimatedEffort),
    finalEffort: asNumberOrZero(raw.finalEffort),
    effortRationale: raw.effortRationale ?? null,
    roleId: raw.roleId ?? null,
    roleName: raw.roleName ?? null,
    fte: asNumber(raw.fte),
    hourlyRate: asNumber(raw.hourlyRate),
    cost: asNumber(raw.cost),
    durationDays: raw.durationDays ?? 0,
    plannedStartDate: dateOnly(raw.plannedStartDate),
    plannedEndDate: dateOnly(raw.plannedEndDate),
    sprintPhase: flagYes(raw.sprintPhaseFlag),
    parallelPhase: flagYes(raw.parallelPhaseFlag),
    devPhase: flagYes(raw.devPhaseFlag),
    remarks: raw.remarks ?? null,
    updatedAt: raw.updatedAt ?? "",
    updatedBy: raw.updatedBy ?? "",
  };
}

export function mapRomSummaryHistory(raw: RomSummaryHistoryApi): RomSummaryHistory {
  const summary = mapRomSummary(raw);
  return {
    id: raw.romHistoryId,
    updatedAt: summary.updatedAt,
    updatedBy: summary.updatedBy,
    plannedStartDate: summary.plannedStartDate || null,
    plannedEndDate: summary.plannedEndDate,
    brCount: summary.brCount,
    brNyCount: summary.brNyCount,
    devEffort: summary.devEffort,
    estTotalEffort: summary.estTotalEffort,
    finalTotalEffort: summary.finalTotalEffort,
    totalDurationDays: summary.totalDurationDays,
    totalDurationWeeks: summary.totalDurationWeeks,
    durationMonths: summary.durationMonths,
    sprintCount: summary.sprintCount,
    manpowerCost: summary.manpowerCost,
    status: summary.status,
    remarks: summary.remarks,
  };
}

export function mapRomPhaseHistory(raw: RomPhaseHistoryApi): RomPhaseHistory {
  const phase = mapRomPhase({ ...raw, romPhId: raw.romPhId });
  return {
    id: raw.romPhHistoryId,
    phaseId: raw.romPhId,
    updatedAt: phase.updatedAt,
    updatedBy: phase.updatedBy,
    name: phase.name,
    percentage: phase.percentage,
    estEffort: phase.estEffort,
    finalEffort: phase.finalEffort,
    effortRationale: phase.effortRationale,
    roleName: phase.roleName,
    fte: phase.fte,
    hourlyRate: phase.hourlyRate,
    cost: phase.cost,
    durationDays: phase.durationDays,
    plannedStartDate: phase.plannedStartDate,
    plannedEndDate: phase.plannedEndDate,
    remarks: phase.remarks,
    parallelPhase: phase.parallelPhase,
  };
}

export function mapRomPreview(raw: RomPreviewApi): DevelopPreview {
  const brNyCount = raw.notYetEstimatedCount ?? 0;
  const messages = raw.messages ?? [];
  const nyMessage = nyAckMessage(brNyCount);
  const changes: string[] = [];
  if (raw.sourceDataChanged) {
    if (
      asNumberOrZero(raw.previousDevelopmentEffort) !==
      asNumberOrZero(raw.currentDevelopmentEffort)
    ) {
      changes.push(
        `Development effort: ${formatChangeHours(raw.previousDevelopmentEffort)} → ${formatChangeHours(raw.currentDevelopmentEffort)} hours.`,
      );
    }
    if (
      (raw.previousBusinessRequirementCount ?? 0) !==
      (raw.currentBusinessRequirementCount ?? 0)
    ) {
      changes.push(
        `Business Requirements: ${raw.previousBusinessRequirementCount ?? 0} → ${raw.currentBusinessRequirementCount ?? 0}.`,
      );
    }
    if (
      (raw.previousNotYetEstimatedCount ?? 0) !==
      (raw.currentNotYetEstimatedCount ?? 0)
    ) {
      changes.push(
        `Not yet estimated: ${raw.previousNotYetEstimatedCount ?? 0} → ${raw.currentNotYetEstimatedCount ?? 0}.`,
      );
    }
  }

  const blockers = messages.filter(
    (message) =>
      message !== nyMessage &&
      message !== RECALCULATION_WARNING &&
      !changes.includes(message),
  );

  return {
    canDevelop: Boolean(raw.canDevelop),
    techStack: raw.technologyStack ?? "—",
    domain: raw.domain ?? "—",
    brCount: raw.businessRequirementCount ?? 0,
    brNyCount,
    devEffort: asNumberOrZero(raw.developmentEffort),
    romExists: Boolean(raw.romExists),
    showPlannedStart: !raw.romExists,
    defaultPlannedStart: dateOnly(raw.plannedStartDate),
    changes,
    blockers: raw.canDevelop ? [] : blockers,
    messages,
    requiresNyAcknowledgement: Boolean(raw.requiresNyAcknowledgement),
    recalculating: Boolean(raw.romExists && raw.sourceDataChanged && raw.canDevelop),
    status: raw.status ? normalizeStatus(raw.status) : null,
  };
}

/** HTTP client for ROM Effort Estimates (`/pms/rom-effort/...`). */
export class RomEffortEstimatesService {
  constructor(private readonly httpClient: HTTP) {}

  async getMetadata(): Promise<RomMetadata> {
    const raw = await this.httpClient.get<RomMetadataApi>(`${BASE}/metadata`);
    const roles: RomRole[] = (raw.roles ?? []).flatMap((role) => {
      if (typeof role.roleId !== "number" || !role.roleName) return [];
      const hourlyRate = asNumber(role.hourlyRate);
      if (hourlyRate == null) return [];
      return [{ id: role.roleId, name: role.roleName, hourlyRate }];
    });
    return {
      statusOptions: (raw.status ?? []).flatMap((item) =>
        item.enum ? [{ enum: item.enum, label: item.label ?? item.enum }] : [],
      ),
      roles,
    };
  }

  async listRoles(): Promise<RomRole[]> {
    const metadata = await this.getMetadata();
    return metadata.roles;
  }

  async getSummary(projectId: number): Promise<RomSummary | null> {
    try {
      const raw = await this.httpClient.get<RomSummaryApi>(
        `${BASE}/project/${projectId}`,
      );
      return mapRomSummary(raw);
    } catch (error) {
      if (isNotFoundError(error)) return null;
      throw error;
    }
  }

  async getPhases(projectId: number): Promise<RomPhase[]> {
    try {
      const raw = await this.httpClient.get<RomPhaseApi[]>(
        `${BASE}/project/${projectId}/phases`,
      );
      return (raw ?? []).map(mapRomPhase).sort((a, b) => a.sequence - b.sequence);
    } catch (error) {
      if (isNotFoundError(error)) return [];
      throw error;
    }
  }

  async getSummaryHistory(projectId: number): Promise<RomSummaryHistory[]> {
    try {
      const raw = await this.httpClient.get<RomSummaryHistoryApi[]>(
        `${BASE}/project/${projectId}/history`,
      );
      return (raw ?? []).map(mapRomSummaryHistory);
    } catch (error) {
      if (isNotFoundError(error)) return [];
      throw error;
    }
  }

  async getPhaseHistory(phaseId: number): Promise<RomPhaseHistory[]> {
    try {
      const raw = await this.httpClient.get<RomPhaseHistoryApi[]>(
        `${BASE}/phases/${phaseId}/history`,
      );
      return (raw ?? []).map(mapRomPhaseHistory);
    } catch (error) {
      if (isNotFoundError(error)) return [];
      throw error;
    }
  }

  async previewDevelop(
    projectId: number,
    input: DevelopRomInput = {},
  ): Promise<DevelopPreview> {
    const raw = await this.httpClient.post<RomPreviewApi>(
      `${BASE}/project/${projectId}/preview`,
      {
        body: {
          plannedStartDate: input.plannedStartDate || null,
          acknowledgeNotYetEstimated: Boolean(input.nyAcknowledged),
          confirmRecalculation: Boolean(input.confirmRecalculation),
        },
      },
    );
    return mapRomPreview(raw);
  }

  async develop(
    projectId: number,
    input: DevelopRomInput = {},
  ): Promise<DevelopRomResult> {
    const raw = await this.httpClient.post<RomDevelopApi>(
      `${BASE}/project/${projectId}/develop`,
      {
        body: {
          plannedStartDate: input.plannedStartDate || null,
          acknowledgeNotYetEstimated: Boolean(input.nyAcknowledged),
          confirmRecalculation: Boolean(input.confirmRecalculation),
        },
      },
    );
    return {
      developed: Boolean(raw.developed),
      message: raw.message?.trim() || "ROM develop finished.",
      warnings: raw.warnings ?? [],
      phaseCount: raw.phaseCount ?? 0,
    };
  }

  async updateSummary(
    projectId: number,
    input: UpdateRomSummaryInput,
  ): Promise<RomSummary> {
    const raw = await this.httpClient.put<RomSummaryApi>(
      `${BASE}/project/${projectId}`,
      {
        body: {
          plannedStartDate: input.plannedStartDate,
          status: input.status,
          remarks: input.remarks,
        },
      },
    );
    return mapRomSummary(raw);
  }

  async updatePhase(
    _projectId: number,
    phaseId: number,
    input: UpdateRomPhaseInput,
  ): Promise<RomPhase> {
    const raw = await this.httpClient.put<RomPhaseApi>(
      `${BASE}/phases/${phaseId}`,
      {
        body: {
          finalEffort: input.finalEffort,
          effortRationale: input.effortRationale || null,
          roleId: input.roleId,
          fte: input.fte,
          remarks: input.remarks,
        },
      },
    );
    return mapRomPhase(raw);
  }
}
